(function () {
  'use strict';

  window.NavigationManager = {
    currentState: {
      mode: null,
      path: null,
      params: {},
      data: {},
    },

    history: [],
    historyIndex: -1,

    init: function () {

      const currentPath = window.location.pathname;
      if (!this.isInternalNavigation(currentPath)) {
        return;
      }

      // Use PopStateCoordinator if available, otherwise fall back to direct listener
      if (window.PopStateCoordinator) {
        window.PopStateCoordinator.register(
          this.handlePopState.bind(this),
          100, // High priority for navigation manager
          'NavigationManager'
        );
      } else {
        window.addEventListener('popstate', this.handlePopState.bind(this));
      }

      document.addEventListener(
        'click',
        this.handleNavigationClick.bind(this),
        true
      );

      // If chat-mode-switcher already initialized, avoid a second initial navigate
      if (window.chatModeState && window.chatModeState.initialized) {
      } else {
        this.navigateToCurrentURL(true);
      }
    },

    handlePopState: function (event) {

      if (event.state && event.state.path) {
        // Force navigation on popstate even if we think we're already there
        // Clear current state to bypass the duplicate check
        this.currentState = {};
        this.navigateToState(event.state, true);
      } else {
        // Force navigation from URL
        this.currentState = {};
        this.navigateToCurrentURL(true);
      }
    },

    handleNavigationClick: function (event) {
      let link = event.target.closest('a');

      if (!link) return;

      const href = link.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('javascript:')) {
        return;
      }

      if (
        link.target === '_blank' ||
        (href.startsWith('http') && !href.includes(window.location.hostname))
      ) {
        return;
      }

      if (this.isInternalNavigation(href)) {
        const isAICharItem = link.classList.contains('ai-character-item');
        const isRoomItem =
          link.classList.contains('mdl-chip') && href.startsWith('/r/');

        if (isAICharItem || isRoomItem) {
          event.preventDefault();
          event.stopPropagation();
          this.navigate(href);
          return false;
        }

        if (link.hasAttribute('data-bypass')) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.navigate(href);
        return false;
      }
    },

    isInternalNavigation: function (url) {
      return (
        url.startsWith('/ai-chat/') ||
        url === '/ai-chat' ||
        url.startsWith('/r/') ||
        url === '/'
      );
    },

    navigate: function (url, skipHistory) {

      const state = this.parseURL(url);
      if (!state) {

        window.location.href = url;
        return;
      }

      if (state.mode === 'chatroom' && !state.params.roomkey) {
        console.warn('[NAV] Missing room key for chatroom navigation, falling back to hard navigation');
        if (skipHistory) {
          window.location.replace(url);
        } else {
          window.location.assign(url);
        }
        return;
      }

      // Prevent redundant navigations to identical state to avoid loops (but not on popstate)
      if (!skipHistory) {
        try {
          var cur = this.currentState || {};
          if (
            cur.path === state.path &&
            cur.mode === state.mode &&
            JSON.stringify(cur.params || {}) ===
              JSON.stringify(state.params || {})
          ) {
            return;
          }
        } catch { /* Error ignored */ }
      }

      if (!skipHistory) {
        // Always push state when navigating normally (not from popstate)
        window.history.pushState(state, '', url);
      }

      // Avoid navigating to slugless /ai-chat which backend may 404
      if (
        state.mode === 'chat' &&
        !state.params.name &&
        (!state.data || !state.data.ai_char)
      ) {
        state.data = state.data || {};
        state.data.ai_char =
          (window.fixtures && window.fixtures.currentAI) ||
          window.DefaultAICharacter ||
          {};
      }
      this.navigateToState(state, skipHistory);
    },

    navigateToCurrentURL: function (skipHistory) {
      const path = window.location.pathname;
      // Stay on homepage path, do not navigate to /ai-chat automatically
      if (path === '/' || path === '/index.html') {
        const state = {
          mode: 'chat',
          path: path,
          params: {},
          data: {
            ai_char:
              (window.fixtures && window.fixtures.currentAI) ||
              window.DefaultAICharacter ||
              {},
          },
        };
        this.navigateToState(state, true);
        return;
      }
      this.navigate(path, skipHistory);
    },

    parseURL: function (url) {
      const state = {
        mode: null,
        path: url,
        params: {},
        data: {},
      };

      if (url.startsWith('/ai-chat/')) {
        state.mode = 'chat';
        state.params.name = decodeURIComponent(url.substring(9));
      } else if (url === '/ai-chat' || url === '/') {
        state.mode = 'chat';
        state.data.ai_char =
          (window.fixtures && window.fixtures.currentAI) ||
          window.DefaultAICharacter || {
            name: 'Isekai narrator',
            description:
              'An omniscient narrator for epic isekai roleplay adventures.',
            greeting:
              'Welcome to a world beyond your imagination. What path will you choose?',
            avatar_file_name: '',
          };
      } else if (url.startsWith('/r/')) {
        state.mode = 'chatroom';
        state.params.roomkey = url.substring(3);
      }

      return state.mode ? state : null;
    },

    navigateToState: async function (state, skipHistory) {

      if (this.currentState.mode) {
        this.saveCurrentState();
      }

      await this.loadStateData(state);

      // Avoid redundant re-renders/loops if state is identical
      try {
        var prev = this.currentState || {};
        var samePath = prev.path === state.path;
        var sameMode = prev.mode === state.mode;
        var sameName =
          (prev?.params?.name || '') === (state?.params?.name || '');
        if (samePath && sameMode && sameName) {
          return;
        }
      } catch { /* Error ignored */ }
      this.currentState = state;

      this.performNavigation(state);
    },

    loadStateData: async function (state) {
      if (state.mode === 'chat' && state.params.name) {
        try {
          var hasAccessor = !!window.dataAccessor;
          var byNameMap =
            hasAccessor && window.dataAccessor.aisByName
              ? window.dataAccessor.aisByName
              : null;
          if (byNameMap && byNameMap[state.params.name]) {
            state.data.ai_char = byNameMap[state.params.name];
          } else if (
            hasAccessor &&
            typeof window.dataAccessor.getAIByName === 'function'
          ) {
            const aiData = await window.dataAccessor.getAIByName(
              state.params.name
            );
            state.data.ai_char = aiData;
          } else {
            const friendlyName = decodeURIComponent(state.params.name).replace(
              /-/g,
              ' '
            );
            var searchBaseUrl =
              (window.fixtures && window.fixtures.search_base_url) || '';
            if (searchBaseUrl && searchBaseUrl.indexOf('localhost') > -1) {
              searchBaseUrl = '';
            }
            const resp = await fetch(
              searchBaseUrl +
                '/api/get-ai-by-name?' +
                new URLSearchParams({ name: friendlyName }),
              {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
              }
            );
            if (resp.ok) {
              const json = await resp.json();
              state.data.ai_char =
                json && json.success && json.data ? json.data : json;
            }
          }
        } catch (err) {
          console.error('[NAV] Failed to load AI character:', err);
          state.data.ai_char = {
            name: state.params.name,
            description: 'Character unavailable',
            greeting: '',
            error: true,
          };
        }
      } else if (state.mode === 'chatroom' && state.params.roomkey) {
        try {
          const response = await $.ajax({
            url: '/api/get-room/' + state.params.roomkey,
            type: 'GET',
            dataType: 'json',
          });

          if (response && response.success) {
            state.data.room_name = response.name;
            state.data.room_ais = response.ais || {};
            state.data.room_id = response.id;
          } else {
            throw new Error('Invalid room response');
          }
        } catch (err) {
          console.error('[NAV] Failed to load room:', err);

          try {
            const fallbackResponse = await $.ajax({
              url: '/api/get-all-rooms',
              type: 'GET',
              dataType: 'json',
            });

            if (fallbackResponse && fallbackResponse.rooms) {
              const room = fallbackResponse.rooms.find(
                (r) => r.room_key === state.params.roomkey
              );
              if (room) {
                state.data.room_name = room.name;
                state.data.room_ais = {};
              } else {
                state.data.room_name = state.params.roomkey || 'Unknown Room';
                state.data.room_ais = {};
                console.warn(
                  '[NAV] Using fallback room data for:',
                  state.params.roomkey
                );
              }
            }
          } catch (fallbackErr) {
            console.error('[NAV] Fallback also failed:', fallbackErr);

            state.data.room_name = state.params.roomkey || 'Unknown Room';
            state.data.room_ais = {};
            console.warn('[NAV] Using emergency fallback room data');
          }
        }
      }
    },

    performNavigation: function (state) {

      try {
        const dialogs = document.querySelectorAll('dialog[open]:not(#login-dialog):not(#signup-dialog)');
        dialogs.forEach((dialog) => dialog.close());
      } catch { /* Error ignored */ }

      if (window.chatModeState && window.chatModeState.mode) {
        this.cleanupMode(window.chatModeState.mode);
      }

      if (state.mode === 'chat') {
        if (!document.querySelector('.chat-log')) {
          window.location.assign(state.path);
          return;
        }
        this.switchToChat(state);
      } else if (state.mode === 'chatroom') {
        if (!document.querySelector('.chat-log')) {
          window.location.assign(state.path);
          return;
        }
        this.switchToChatroom(state);
      }

      this.updateUIForMode(state.mode);

      $(window).trigger('routechange', [state]);
    },

    switchToChat: function (state) {

      if (state.data.ai_char) {
        fixtures.currentAI = state.data.ai_char;
        fixtures.room_name = null;
        fixtures.room_ais = {};
      }

      const config = {
        ai_char: state.data.ai_char || fixtures.currentAI,
      };

      if (
        window.switchChatMode &&
        typeof window.switchChatMode === 'function'
      ) {
        window.switchChatMode('chat', config, true);
      } else {
        console.warn('[NAV] switchChatMode not available, using fallback');

        $('#chatroom-ais-in-room').hide();
        $('#edit-characters-btn').hide();
        $('#save-button').show();

        if (fixtures.currentAI) {
          var avatarFileName = fixtures.currentAI.avatar_file_name;
          if (avatarFileName && avatarFileName !== 'None') {
            avatarFileName = avatarFileName.trim();
            $('.chat-one-to-one-icon').attr(
              'src',
              'https://netwrckstatic.netwrck.com/static/uploads/' +
                avatarFileName
            );
          } else {
            $('.chat-one-to-one-icon').attr(
              'src',
              'https://netwrckstatic.netwrck.com/static/img/netwrck-logo-colord256.webp'
            );
          }
          $('.chat-one-to-one-name').text(fixtures.currentAI.name);
          $('.chat-one-to-one-name-tooltip').text(
            fixtures.currentAI.description
          );
        }

        if (
          window.ReRenderCurrentGame &&
          typeof window.ReRenderCurrentGame === 'function'
        ) {
          window.ReRenderCurrentGame();
        }
      }

      if (state.data.ai_char && state.data.ai_char.name) {
        document.title = state.data.ai_char.name + ' - AI Chat';
      }
    },

    switchToChatroom: function (state) {

      fixtures.room_name = state.data.room_name;
      fixtures.room_ais = state.data.room_ais || {};
      fixtures.currentAI = null;

      const config = {
        room_name: state.data.room_name,
        room_ais: state.data.room_ais || {},
      };

      if (
        window.switchChatMode &&
        typeof window.switchChatMode === 'function'
      ) {
        window.switchChatMode('chatroom', config, true);

        if (state.data.room_name) {
          $('.chat-one-to-one-name').text('Room: ' + state.data.room_name);
          $('.chat-one-to-one-name-tooltip').text(
            'Chatroom with multiple AI characters'
          );
        }
      } else {
        console.warn('[NAV] switchChatMode not available, using fallback');

        $('#chatroom-ais-in-room').show();
        $('#edit-characters-btn').show();
        $('#save-button').hide();

        $('.chat-one-to-one-name').text('Room: ' + fixtures.room_name);
        $('.chat-one-to-one-name-tooltip').text(
          'Chatroom with multiple AI characters'
        );
        $('.chat-one-to-one-icon').attr(
          'src',
          'https://netwrckstatic.netwrck.com/static/img/netwrck-logo-colord256.webp'
        );

        if (
          window.renderAIsInRoom &&
          typeof window.renderAIsInRoom === 'function'
        ) {
          window.renderAIsInRoom(fixtures.room_ais);
        }
        if (
          window.renderAIsInRoomList &&
          typeof window.renderAIsInRoomList === 'function'
        ) {
          window.renderAIsInRoomList(fixtures.room_ais);
        }

        if (
          window.ReRenderCurrentGame &&
          typeof window.ReRenderCurrentGame === 'function'
        ) {
          window.ReRenderCurrentGame();
        }

        if (state.data.room_name) {
          this.setupRoomEventSource(state.data.room_name);
        } else {
          console.warn('[NAV] No room name available for EventSource setup');
        }
      }

      if (state.data.room_name) {
        document.title = 'Room: ' + state.data.room_name;
      }
    },

    setupRoomEventSource: function (roomName) {
      if (window.chatModeState && window.chatModeState.roomEventSource) {
        window.chatModeState.roomEventSource.close();
      }

      var searchBaseUrl = fixtures.search_base_url || '';
      if (searchBaseUrl && searchBaseUrl.indexOf('localhost') > -1) {
        searchBaseUrl = '';
      }

      if (!roomName || !String(roomName).trim()) {
        console.warn(
          '[NAV] Refusing to open live subscribe without a valid room name'
        );
        return;
      }
      const eventSource = new EventSource(
        searchBaseUrl +
          '/api/live/subscribe?channel=' +
          encodeURIComponent(roomName)
      );

      eventSource.onmessage = function (e) {
        try {
          var data = JSON.parse(e.data);
          if (data.text && data.username) {
            var srcImg = getSrcImgForCharacter({
              avatar_file_name: data.avatar_file_name,
            });
            appendToChatLog(
              `<img class="chat-log-avatar-icon" src="${srcImg}" alt="${data.username}" width="100" height="100"><p class="chat-group-name" tabindex="0">${data.username}</p><div class="chat-log__item"><div class="chat-log__message" contenteditable="true" spellcheck="false">${data.text}</div></div>`,
              undefined,
              data
            );
            if (typeof playNotification === 'function') {
              playNotification();
            }
          }
        } catch (err) {
          console.error('[NAV] Invalid live message:', err);
        }
      };

      if (!window.chatModeState) {
        window.chatModeState = {};
      }
      window.chatModeState.roomEventSource = eventSource;
    },

    cleanupMode: function (mode) {
      if (window.chatModeState && window.chatModeState.roomEventSource) {
        window.chatModeState.roomEventSource.close();
        window.chatModeState.roomEventSource = null;
      }

      $('.typing-indicator').remove();
    },

    updateUIForMode: function (mode) {
      $('.nav-item').removeClass('active');
      if (mode === 'chat') {
        $('.nav-item[data-mode="chat"]').addClass('active');
      } else if (mode === 'chatroom') {
        $('.nav-item[data-mode="chatroom"]').addClass('active');
      }
    },

    saveCurrentState: function () {
      if (this.currentState.mode === 'chat') {
        this.currentState.data.messages = this.extractChatMessages();
      } else if (this.currentState.mode === 'chatroom') {
        this.currentState.data.messages = this.extractChatMessages();
      }
    },

    extractChatMessages: function () {
      const messages = [];
      $('.chat-log .chat-log__item').each(function () {
        const $item = $(this);
        messages.push({
          html: $item.prop('outerHTML'),
          own: $item.hasClass('chat-log__item--own'),
          text: $item.find('.chat-log__message').text(),
        });
      });
      return messages;
    },

    goToChat: function (aiName) {
      const url = aiName
        ? `/ai-chat/${encodeURIComponent(aiName)}`
        : '/ai-chat';
      this.navigate(url);
    },

    goToRoom: function (roomKey) {
      if (!roomKey) {
        if (window.fixtures && window.fixtures.room_key) {
          roomKey = window.fixtures.room_key;
        } else {
          window.location.assign('/r');
          return;
        }
      }
      const url = `/r/${roomKey}`;
      this.navigate(url);
    },

    goBack: function () {
      window.history.back();
    },

    goForward: function () {
      window.history.forward();
    },
  };

  // Wait for jQuery to be available
  if (typeof $ !== 'undefined') {
    $(document).ready(function () {
      setTimeout(function () {
        window.NavigationManager.init();
      }, 100);
    });
  } else {
    // Fallback if jQuery isn't loaded yet
    document.addEventListener('DOMContentLoaded', function () {
      setTimeout(function () {
        window.NavigationManager.init();
      }, 100);
    });
  }
})();
